import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';

const AchievementsManagement = () => {
  const [achievements, setAchievements] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    achievement_date: '',
    image_url: '',
    published: true,
  });

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await fetch('http://localhost/api/achievements.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Başarılar yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // is_published değerlerini normalize et
      const normalizedData = data.map(item => ({
        ...item,
        is_published: item.is_published === 1 || item.is_published === '1' || item.is_published === true ? 1 : 0,
      }));
      
      setAchievements(normalizedData);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      toast.error('Başarılar yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const deleteOldImage = async (imageUrl) => {
    if (!imageUrl || !imageUrl.includes('/uploads/')) return;
    
    try {
      await fetch('http://localhost/api/delete-image.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl }),
      });
    } catch (error) {
      // Sessizce hata yut (resim silinmese bile devam et)
      console.error('Error deleting old image:', error);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Dosya boyutu kontrolü (25MB)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Dosya boyutu 25MB\'dan büyük olamaz');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    // Eski resmi sakla (yeni resim yüklendikten sonra silmek için)
    const oldImageUrl = formData.image_url;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Görsel yüklenirken hata oluştu');
        return;
      }

      // URL'yi kontrol et ve düzelt
      let imageUrl = data.url || data.image_url || '';

      // Eğer sadece filename varsa, tam URL oluştur
      if (!imageUrl && data.filename) {
        imageUrl = `/uploads/${data.filename}`;
      }

      // Relative path'i tam URL'ye çevir
      if (imageUrl && !imageUrl.startsWith('http')) {
        if (imageUrl.startsWith('/uploads/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else if (imageUrl.startsWith('/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else {
          imageUrl = `http://localhost/uploads/${imageUrl}`;
        }
      }

      if (imageUrl) {
        // Yeni resim başarıyla yüklendi, eski resmi sil
        if (oldImageUrl && oldImageUrl !== imageUrl && oldImageUrl.includes('/uploads/')) {
          await deleteOldImage(oldImageUrl);
        }
        
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Görsel başarıyla yüklendi');
      } else {
        toast.error('Görsel URL alınamadı');
      }
    } catch (error) {
      toast.error('Görsel yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingItem
        ? `http://localhost/api/achievements.php?id=${editingItem.id}`
        : 'http://localhost/api/achievements.php';
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          achievement_date: formData.achievement_date || null,
          image_url: formData.image_url || '',
          is_published: formData.published ? 1 : 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Başarı kaydedilirken hata oluştu');
        return;
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      toast.success(editingItem ? 'Başarı başarıyla güncellendi' : 'Başarı başarıyla eklendi');
      setIsModalOpen(false);
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        achievement_date: '',
        image_url: '',
        published: true,
      });
      fetchAchievements();
    } catch (error) {
      console.error('Error saving achievement:', error);
      toast.error('Başarı kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      achievement_date: item.achievement_date || '',
      image_url: item.image_url || '',
      published: item.is_published === 1 || item.is_published === '1' || item.is_published === true,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/achievements.php?id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Başarı silinirken hata oluştu');
        return;
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      toast.success('Başarı başarıyla silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      fetchAchievements();
    } catch (error) {
      console.error('Error deleting achievement:', error);
      toast.error('Başarı silinirken hata oluştu');
    }
  };

  const togglePublished = async (item) => {
    // Mevcut durumu kontrol et - farklı formatları destekle
    const currentPublished = item.is_published === 1 || item.is_published === '1' || item.is_published === true;
    const newPublishedValue = currentPublished ? 0 : 1;
    
    // State'i anında güncelle (optimistic update)
    setAchievements(prev => prev.map(achievementItem => 
      achievementItem.id === item.id 
        ? { ...achievementItem, is_published: newPublishedValue }
        : achievementItem
    ));
    
    try {
      const response = await fetch(`http://localhost/api/achievements.php?id=${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: item.title,
          description: item.description,
          achievement_date: item.achievement_date || null,
          image_url: item.image_url || '',
          is_published: newPublishedValue,
        }),
      });

      if (!response.ok) {
        // Hata durumunda state'i geri al
        setAchievements(prev => prev.map(achievementItem => 
          achievementItem.id === item.id 
            ? { ...achievementItem, is_published: currentPublished ? 1 : 0 }
            : achievementItem
        ));
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Güncelleme sırasında hata oluştu');
        return;
      }

      toast.success('Başarı durumu güncellendi');
      // Verileri yeniden çek
      fetchAchievements();
    } catch (error) {
      // Hata durumunda state'i geri al
      setAchievements(prev => prev.map(achievementItem => 
        achievementItem.id === item.id 
          ? { ...achievementItem, is_published: currentPublished ? 1 : 0 }
          : achievementItem
      ));
      console.error('Error toggling published:', error);
      toast.error('Güncelleme sırasında hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // YYYY-MM-DD formatındaki tarihi parse et
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      achievement_date: '',
      image_url: '',
      published: true,
    });
    setIsModalOpen(true);
  };

  return (
    <AdminLayout
      addButton={
        <Button
          onClick={openAddModal}
          data-testid="add-achievement-btn"
          className="bg-[#C0A062] hover:bg-[#9a7d4a] text-white flex items-center gap-2"
        >
          <Plus size={20} />
          Yeni Başarı
        </Button>
      }
    >
      <div>
        <div className="mt-6">
        {achievements.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz başarı yok</h3>
            <p className="text-gray-600 mb-6">Yeni bir başarı ekleyerek başlayın</p>
            <Button onClick={openAddModal} className="bg-[#C0A062] hover:bg-[#9a7d4a]">
              İlk Başarıyı Ekle
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {achievements.map((item) => (
              <div
                key={item.id}
                data-testid={`achievement-card-${item.id}`}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all"
              >
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                      {formatDate(item.achievement_date)}
                    </span>
                    {item.is_published ? (
                      <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1">
                        <Eye size={12} />
                        Yayında
                      </span>
                    ) : (
                      <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full flex items-center gap-1">
                        <EyeOff size={12} />
                        Taslak
                      </span>
                    )}
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4 line-clamp-3">{item.description}</p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <Button
                      onClick={() => handleEdit(item)}
                      data-testid={`edit-achievement-${item.id}`}
                      variant="outline"
                      size="sm"
                      className="flex-1 flex items-center justify-center gap-1 py-3 sm:py-1.5 text-sm sm:text-xs"
                    >
                      <Edit size={18} className="sm:w-4 sm:h-4" />
                      Düzenle
                    </Button>
                    <Button
                      onClick={() => togglePublished(item)}
                      data-testid={`toggle-achievement-${item.id}`}
                      variant="outline"
                      size="sm"
                      className="py-3 sm:py-1.5"
                    >
                      {item.is_published ? <EyeOff size={18} className="sm:w-4 sm:h-4" /> : <Eye size={18} className="sm:w-4 sm:h-4" />}
                    </Button>
                    <Button
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-achievement-${item.id}`}
                      variant="destructive"
                      size="sm"
                      className="py-3 sm:py-1.5"
                    >
                      <Trash2 size={18} className="sm:w-4 sm:h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Başarıyı Düzenle' : 'Yeni Başarı Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
              <Input
                data-testid="achievement-title-input"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Başarı başlığı"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Açıklama *</label>
              <Textarea
                data-testid="achievement-description-input"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Başarı açıklaması"
                rows={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tarih *</label>
              <Input
                type="date"
                data-testid="achievement-date-input"
                name="achievement_date"
                value={formData.achievement_date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Görsel Yükle</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Sol: Yükleme Alanı */}
                <div
                  className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#C0A062] hover:bg-[#C0A062]/5 transition-all cursor-pointer group"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.add('border-[#C0A062]', 'bg-[#C0A062]/10');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-[#C0A062]', 'bg-[#C0A062]/10');
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-[#C0A062]', 'bg-[#C0A062]/10');
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      await handleImageUpload(file);
                    } else {
                      toast.error('Lütfen geçerli bir resim dosyası seçin');
                    }
                  }}
                  onClick={() => {
                    const input = document.getElementById('achievement-image-upload-input');
                    input?.click();
                  }}
                >
                  <input
                    id="achievement-image-upload-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleImageUpload(file);
                      }
                    }}
                  />
                  {!formData.image_url ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-gray-100 group-hover:bg-[#C0A062]/10 flex items-center justify-center transition-colors">
                        <Upload className="w-8 h-8 text-gray-400 group-hover:text-[#C0A062] transition-colors" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Görsel sürükle ve bırak
                        </p>
                        <p className="text-xs text-gray-500">
                          veya tıklayarak seç
                        </p>
                      </div>
                      <p className="text-xs text-gray-400">
                        PNG, JPG, GIF (max. 25MB)
                      </p>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Görseli değiştirmek için tıklayın</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sağ: Önizleme */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
                  {formData.image_url ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <img 
                        key={formData.image_url}
                        src={formData.image_url} 
                        alt="Önizleme" 
                        className="max-w-full max-h-48 object-contain rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.parentElement.querySelector('.image-error');
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      />
                      <div className="image-error hidden text-center text-red-500 text-xs">
                        <p>Görsel yüklenemedi</p>
                        <p className="text-gray-400 break-all text-[10px] mt-1">{formData.image_url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, image_url: '' }));
                          const input = document.getElementById('achievement-image-upload-input');
                          if (input) input.value = '';
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Önizleme</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                data-testid="achievement-published-checkbox"
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="w-4 h-4 text-[#C0A062] border-gray-300 rounded focus:ring-[#C0A062]"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">Hemen yayınla</label>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                data-testid="save-achievement-btn"
                className="flex-1 bg-[#C0A062] hover:bg-[#9a7d4a]"
              >
                {editingItem ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                data-testid="cancel-achievement-btn"
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md w-[95vw] sm:w-full mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu başarıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AchievementsManagement;